/* eslint-disable max-len */
import React, { createContext, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Components } from 'src/configs/components/components';
import { getCanonicalUrl, getSeoState, hasSeoMeta, SeoHelmet, toSeoMeta } from 'src/helpers/seo';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { getCategoryPage } from 'src/services/cmsApi';
import { CmsPageContent } from 'src/types/cms/cmsCommonTypes';
import { CmsCategoryPageFields } from 'src/types/cms/cmsPageTypes';
import '../styles.scss';

type Props = {
  categorySlug: string;
  type: string;
  categoryName: string;
  settings: {
    firstSection: { [key: string]: any };
    secondSection: { [key: string]: any };
    thirdSection: { [key: string]: any };
  };
};

export const CategoryPageContext = createContext<CmsPageContent<CmsCategoryPageFields> | null>(null);
export const CategoryPageLoadedContext = createContext<boolean>(false);

const CategoryPageConstructor: FC<Props> = ({ settings, type, categorySlug, categoryName }) => {
  const sessionRecorder = useSessionRecorder();
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const [categoryPage, setCategoryPage] = useState<CmsPageContent<CmsCategoryPageFields> | null>(null);
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const meta = toSeoMeta(categoryPage?.meta);
  const rawSeoConfig = process.env.REACT_APP_SEO_META as string | undefined;
  const { meta: overrideMeta } = getSeoState(pathname, '', rawSeoConfig);
  const hasBackendMeta = hasSeoMeta(meta);
  const hasOverride = hasSeoMeta(overrideMeta);

  const renderCategoryPageWidgets = (line: any): JSX.Element => {
    if (!line || typeof line !== 'object') {
      return <></>;
    }

    const orderIndex: any = {};

    Object.keys(line).forEach((key: string) => (orderIndex[`${key}`] = line[key].priority));

    const widgetLine = Object.keys(line)
      .sort((a: string, b: string) => orderIndex[a] - orderIndex[b])
      .filter((key: string) => key !== 'styles')
      .map((key: string, index: number) => {
        const Component = Components[key];
        if (!Component) {
          return null;
        }

        return React.createElement(Component, {
          settings: line[key],
          type,
          key: `${key}_${index}`,
        });
      });

    return <>{widgetLine}</>;
  };

  useEffect(() => {
    sessionRecorder?.trackEvent('Category page view', {
      categorySlug,
    });
  }, []);

  // CMS category_page content is keyed by the category name (the URL uses the slug).
  useEffect(() => {
    getCategoryPage(i18n.language, categoryName)
      .then((res) => {
        setCategoryPage(res.result?.content || null);
      })
      .finally(() => setPageLoaded(true));
  }, [categoryName, i18n.language]);

  return (
    <CategoryPageContext.Provider value={categoryPage}>
      <CategoryPageLoadedContext.Provider value={pageLoaded}>
        <div className={'page_container'}>
          {!hasOverride && hasBackendMeta && (
            <SeoHelmet category={categorySlug} meta={meta} canonicalUrl={getCanonicalUrl(pathname)} />
          )}
          <div className={'page_container_first_section'}>{renderCategoryPageWidgets(settings?.firstSection)}</div>
          <div className={'page_container_second_section'} style={settings?.secondSection?.styles}>
            {renderCategoryPageWidgets(settings?.secondSection)}
          </div>
          <div className={'page_container_third_section'}>{renderCategoryPageWidgets(settings?.thirdSection)}</div>
        </div>
      </CategoryPageLoadedContext.Provider>
    </CategoryPageContext.Provider>
  );
};
export default CategoryPageConstructor;
